/**
 * Emergent Marketplace — Phase 2
 * OrderHistory.jsx + OrderDetail.jsx
 * Architectural Layer: Presentation → Customer
 *
 * Customer view of all past orders. Expandable order detail.
 * Digital download access inline. Multi-vendor order tracking.
 * Design: Warm document — cream, charcoal, sage confirmation states.
 * Font: Lora (serif headings) + Work Sans (body)
 */

import { useState, useEffect, useRef } from "react";
import {
  Package, Download, BookOpen, Calendar, ChevronDown, ChevronUp,
  CheckCircle, Clock, Truck, XCircle, RefreshCw, ExternalLink,
  ArrowLeft, ShoppingBag, Star
} from "lucide-react";
import { apiFetch } from "./api";

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,600;1,400&family=Work+Sans:wght@300;400;500;600&display=swap');

  .oh-root {
    --cream: #faf8f3;
    --cream-dark: #f0ebe0;
    --charcoal: #1e1e1e;
    --charcoal-mid: #4a4a4a;
    --charcoal-light: #8a8a8a;
    --border: #e2dbd0;
    --surface: #ffffff;
    --sage: #2d6b4a;
    --sage-bg: #eef5f1;
    --sage-border: #b8d6c3;
    --amber: #b07010;
    --amber-bg: #fef8ea;
    --amber-border: #f0d080;
    --blue: #1a5fa8;
    --blue-bg: #eef4fd;
    --red: #b02020;
    --red-bg: #fdf0f0;
    font-family: 'Work Sans', sans-serif;
    background: var(--cream);
    color: var(--charcoal);
    min-height: 100vh;
  }

  /* Header */
  .oh-header {
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    padding: 20px 28px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .oh-header h1 {
    font-family: 'Lora', serif;
    font-size: 22px;
    font-weight: 600;
    color: var(--charcoal);
    margin: 0 0 2px;
  }

  .oh-header p { font-size: 13px; color: var(--charcoal-light); margin: 0; }

  /* Filter tabs */
  .oh-filters {
    display: flex;
    gap: 6px;
    padding: 16px 28px 12px;
    overflow-x: auto;
  }

  .oh-filter-btn {
    padding: 6px 16px;
    border-radius: 20px;
    border: 1px solid var(--border);
    background: var(--surface);
    font-family: 'Work Sans', sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: var(--charcoal-light);
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s;
  }

  .oh-filter-btn.active {
    background: var(--charcoal);
    border-color: var(--charcoal);
    color: white;
  }

  /* Order list */
  .oh-list { padding: 8px 28px 40px; display: flex; flex-direction: column; gap: 12px; }

  .oh-order-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 14px;
    overflow: hidden;
    transition: box-shadow 0.15s;
  }

  .oh-order-card:hover { box-shadow: 0 3px 16px rgba(0,0,0,0.06); }

  /* Order card header */
  .oh-order-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    cursor: pointer;
    user-select: none;
  }

  .oh-order-head-left { display: flex; flex-direction: column; gap: 4px; }

  .oh-order-ref {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--charcoal-light);
  }

  .oh-order-date {
    font-family: 'Lora', serif;
    font-size: 16px;
    font-weight: 600;
    color: var(--charcoal);
    margin: 0;
  }

  .oh-order-head-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
  }

  .oh-order-total {
    font-size: 17px;
    font-weight: 600;
    color: var(--charcoal);
  }

  /* Status pill */
  .oh-status {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .oh-status-paid       { background: var(--sage-bg);  color: var(--sage);  border: 1px solid var(--sage-border); }
  .oh-status-pending    { background: var(--amber-bg); color: var(--amber); border: 1px solid var(--amber-border); }
  .oh-status-shipped    { background: var(--blue-bg);  color: var(--blue);  border: 1px solid #c0d8f4; }
  .oh-status-delivered  { background: var(--sage-bg);  color: var(--sage);  border: 1px solid var(--sage-border); }
  .oh-status-cancelled  { background: var(--red-bg);   color: var(--red);   border: 1px solid #f4c0c0; }
  .oh-status-refunded   { background: var(--red-bg);   color: var(--red);   border: 1px solid #f4c0c0; }
  .oh-status-processing { background: var(--blue-bg);  color: var(--blue);  border: 1px solid #c0d8f4; }

  /* Expanded order detail */
  .oh-order-detail {
    border-top: 1px solid var(--cream-dark);
    animation: oh-expand 0.2s ease;
  }

  @keyframes oh-expand {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* Vendor split section */
  .oh-vendor-split {
    border-bottom: 1px solid var(--cream-dark);
  }

  .oh-vendor-split:last-child { border-bottom: none; }

  .oh-vendor-split-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 20px 10px;
    background: var(--cream);
  }

  .oh-vendor-split-name-row {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .oh-vendor-avatar {
    width: 28px; height: 28px;
    border-radius: 7px;
    background: linear-gradient(135deg, #2d6b50, #1a4a3a);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .oh-vendor-split-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--charcoal);
  }

  .oh-fulfillment-pill {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 20px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .oh-fulfillment-unfulfilled { background: var(--cream-dark); color: var(--charcoal-light); }
  .oh-fulfillment-shipped     { background: var(--blue-bg); color: var(--blue); }
  .oh-fulfillment-delivered   { background: var(--sage-bg); color: var(--sage); }

  /* Items in split */
  .oh-split-item {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px 20px;
    border-top: 1px solid var(--cream-dark);
  }

  .oh-split-item-icon {
    width: 40px; height: 40px;
    border-radius: 8px;
    background: var(--cream-dark);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: var(--charcoal-light);
  }

  .oh-split-item-name {
    flex: 1;
    font-size: 14px;
    color: var(--charcoal);
    line-height: 1.3;
  }

  .oh-split-item-meta {
    font-size: 12px;
    color: var(--charcoal-light);
    margin-top: 2px;
  }

  .oh-split-item-price {
    font-size: 14px;
    font-weight: 600;
    color: var(--charcoal);
    white-space: nowrap;
  }

  /* Download button */
  .oh-download-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 8px;
    border: 1px solid var(--sage-border);
    background: var(--sage-bg);
    color: var(--sage);
    font-family: 'Work Sans', sans-serif;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s;
    text-decoration: none;
    white-space: nowrap;
  }

  .oh-download-btn:hover { opacity: 0.85; }

  /* Tracking */
  .oh-tracking-info {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: var(--blue-bg);
    border-radius: 8px;
    margin: 0 20px 12px;
    font-size: 12px;
    color: var(--blue);
  }

  /* Order summary footer */
  .oh-order-summary-footer {
    padding: 14px 20px;
    background: var(--cream);
    border-top: 1px solid var(--cream-dark);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .oh-summary-amounts {
    display: flex;
    flex-direction: column;
    gap: 3px;
    font-size: 13px;
  }

  .oh-summary-total {
    font-size: 16px;
    font-weight: 700;
    color: var(--charcoal);
    font-family: 'Lora', serif;
  }

  /* Review prompt */
  .oh-review-prompt {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: var(--amber-bg);
    border: 1px solid var(--amber-border);
    border-radius: 10px;
    margin: 12px 20px;
    font-size: 13px;
    color: var(--amber);
    cursor: pointer;
    transition: opacity 0.15s;
  }

  .oh-review-prompt:hover { opacity: 0.85; }

  /* Empty state */
  .oh-empty {
    text-align: center;
    padding: 60px 32px;
  }

  .oh-empty-icon {
    width: 64px; height: 64px;
    border-radius: 50%;
    background: var(--cream-dark);
    border: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    color: var(--charcoal-light);
  }

  /* Chevron btn */
  .oh-expand-btn {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--charcoal-light);
    padding: 4px;
    display: flex;
  }
`;

// ─── Constants & Mock Data ─────────────────────────────────────────────────────

const ORDER_STATUS_CONFIG = {
  pending:    { label: "Pending",    icon: Clock,        class: "oh-status-pending" },
  paid:       { label: "Paid",       icon: CheckCircle,  class: "oh-status-paid" },
  processing: { label: "Processing", icon: RefreshCw,    class: "oh-status-processing" },
  shipped:    { label: "Shipped",    icon: Truck,        class: "oh-status-shipped" },
  delivered:  { label: "Delivered",  icon: CheckCircle,  class: "oh-status-delivered" },
  cancelled:  { label: "Cancelled",  icon: XCircle,      class: "oh-status-cancelled" },
  refunded:   { label: "Refunded",   icon: XCircle,      class: "oh-status-refunded" },
};

const FULFILLMENT_CONFIG = {
  unfulfilled: { label: "Processing",   class: "oh-fulfillment-unfulfilled" },
  pending:     { label: "Preparing",    class: "oh-fulfillment-unfulfilled" },
  shipped:     { label: "Shipped",      class: "oh-fulfillment-shipped" },
  delivered:   { label: "Delivered",    class: "oh-fulfillment-delivered" },
  completed:   { label: "Available now",class: "oh-fulfillment-delivered" },
  cancelled:   { label: "Cancelled",    class: "oh-fulfillment-unfulfilled" },
  refunded:    { label: "Refunded",     class: "oh-fulfillment-unfulfilled" },
};

const PRODUCT_ICONS = {
  physical: Package,
  digital:  Download,
  course:   BookOpen,
  session:  Calendar,
};

const MOCK_ORDERS = [
  {
    id: "ord1",
    status: "paid",
    created_at: "2026-03-08T14:22:00Z",
    payment: { amount_total: 222, paid_at: "2026-03-08T14:23:00Z" },
    vendor_splits: [
      {
        vendor_id: "v1", vendor_name: "Sacred Roots",
        fulfillment_status: "shipped",
        tracking_number: "9400111899223456789",
        subtotal: 102,
        items: [
          { product_id: "1", title: "Morning Awakening Series",  type: "digital",  qty: 1, price: 27,  download_url: "/downloads/morning-series.zip" },
          { product_id: "2", title: "Selenite Charging Bowl",    type: "physical", qty: 2, price: 48 },
        ],
      },
      {
        vendor_id: "v2", vendor_name: "The Still Point",
        fulfillment_status: "unfulfilled",
        tracking_number: null,
        subtotal: 120,
        items: [
          { product_id: "4", title: "1:1 Clarity Session (60 min)", type: "session", qty: 1, price: 120 },
        ],
      },
    ],
  },
  {
    id: "ord2",
    status: "delivered",
    created_at: "2026-02-14T09:10:00Z",
    payment: { amount_total: 197, paid_at: "2026-02-14T09:11:00Z" },
    vendor_splits: [
      {
        vendor_id: "v1", vendor_name: "Sacred Roots",
        fulfillment_status: "delivered",
        tracking_number: "9400111899223456000",
        subtotal: 197,
        items: [
          { product_id: "3", title: "Conscious Living: 21 Days", type: "course", qty: 1, price: 197, download_url: "/courses/conscious-living" },
        ],
      },
    ],
  },
];

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

const formatRef = (id) => `#${id.slice(-6).toUpperCase()}`;

// ─── Return request (the buyer asks the STORE — the platform only rules on escalation) ──────

function ReturnRequestBlock({ receipt, myRequest, onDone }) {
  const [open, setOpen]         = useState(false);
  const [reason, setReason]     = useState("");
  const [amount, setAmount]     = useState("");
  const [issueType, setIssue]   = useState("");
  const [wanted, setWanted]     = useState("refund");
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState("");
  const [policy, setPolicy]     = useState(null);
  const photoInput              = useRef(null);

  const store = receipt.vendor_store_name || "the store";

  useEffect(() => {
    if (myRequest) return;
    apiFetch(`/returns/eligibility/${receipt.id}`).then(setPolicy).catch(() => setPolicy(null));
  }, [receipt.id, myRequest]);

  const submit = async () => {
    if (!issueType) { setError("Pick what went wrong so the store knows what they're looking at"); return; }
    if (!reason.trim()) { setError(`Please tell ${store} what's wrong`); return; }
    // People type "$27.00" or "27,00" — clean it before parsing so a stray symbol
    // can't silently turn a partial request into a full one.
    const cleaned = amount.replace(/[^0-9.]/g, "");
    const parsed = cleaned ? parseFloat(cleaned) : null;
    if (amount.trim() && (parsed === null || Number.isNaN(parsed) || parsed <= 0)) {
      setError("Enter the amount as a number, for example 27.00"); return;
    }
    const max = receipt.subtotal || 0;
    if (parsed !== null && max > 0 && parsed > max) {
      setError(`That's more than this receipt's $${max.toFixed(2)} — enter ${max.toFixed(2)} or less`);
      return;
    }
    setBusy(true); setError("");
    try {
      await apiFetch("/returns", {
        method: "POST",
        body: JSON.stringify({
          sub_order_id: receipt.id,
          amount: parsed,
          reason: reason.trim(),
          issue_type: issueType,
          resolution: wanted,
        }),
      });
      setOpen(false); setReason(""); setAmount(""); setIssue("");
      onDone?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setBusy(true); setError("");
    try {
      const body = new FormData();
      body.append("file", file);
      const token = localStorage.getItem("token");
      const res = await fetch(`/api/returns/${myRequest.id}/evidence`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "That photo could not be uploaded" }));
        throw new Error(typeof err.detail === "string" ? err.detail : "That photo could not be uploaded");
      }
      onDone?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
      if (photoInput.current) photoInput.current.value = "";
    }
  };

  const escalate = async () => {
    setBusy(true); setError("");
    try {
      await apiFetch(`/returns/${myRequest.id}/escalate`, {
        method: "POST", body: JSON.stringify({ note: null }),
      });
      onDone?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (myRequest) {
    const label = {
      requested: `Sent to ${store} — waiting on them`,
      approved:  "Approved — refund processing",
      refunded:  `Refunded $${(myRequest.amount || 0).toFixed(2)}`,
      declined:  `Declined by ${store}`,
      escalated: "Our team is reviewing this",
      failed:    "Refund needs attention — our team has been notified",
    }[myRequest.status] || myRequest.status;

    const photos = myRequest.evidence || [];
    const canAddPhotos = !["refunded", "approved"].includes(myRequest.status);

    return (
      <div style={{ padding: "10px 20px 14px" }} data-testid={`return-state-${receipt.order_number}`}>
        <div style={{
          background: myRequest.status === "declined" ? "var(--red-bg)" : "var(--amber-bg)",
          border: "1px solid var(--amber-border)", borderRadius: 10, padding: "10px 14px", fontSize: 13,
        }}>
          <strong>{label}</strong>
          {myRequest.issue_label && (
            <div style={{ marginTop: 4, color: "var(--charcoal-mid)" }} data-testid="return-issue-label">
              {myRequest.issue_label} · you asked for a {myRequest.resolution === "replacement" ? "replacement" : "refund"}
            </div>
          )}
          {myRequest.vendor_note && (
            <div style={{ marginTop: 4, color: "var(--charcoal-mid)" }}>{store}: {myRequest.vendor_note}</div>
          )}
          {myRequest.execution?.restocking_fee > 0 && (
            <div style={{ marginTop: 4, color: "var(--charcoal-mid)" }}>
              Includes a ${myRequest.execution.restocking_fee.toFixed(2)} restocking fee from this store's policy.
            </div>
          )}
          {myRequest.return_label?.label_url && (
            <div style={{ marginTop: 8 }}>
              <a
                href={myRequest.return_label.label_url}
                target="_blank"
                rel="noreferrer"
                style={{ fontWeight: 600, color: "inherit" }}
                data-testid="buyer-return-label-link"
              >
                Print your prepaid return label ({myRequest.return_label.carrier} {myRequest.return_label.service})
              </a>
            </div>
          )}

          {photos.length > 0 && (
            <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }} data-testid="return-evidence-list">
              {photos.map((url, i) => (
                <a key={`${url}-${i}`} href={url} target="_blank" rel="noreferrer">
                  <img
                    src={url}
                    alt={`Photo ${i + 1}`}
                    style={{ width: 54, height: 54, objectFit: "cover", borderRadius: 8, border: "1px solid var(--border)" }}
                  />
                </a>
              ))}
            </div>
          )}

          {canAddPhotos && (
            <div style={{ marginTop: 10 }}>
              <input
                ref={photoInput}
                type="file"
                accept="image/*"
                onChange={e => uploadPhoto(e.target.files?.[0])}
                style={{ display: "none" }}
                data-testid="return-evidence-input"
              />
              <button
                onClick={() => photoInput.current?.click()}
                disabled={busy}
                style={{
                  padding: "6px 12px", borderRadius: 8, border: "1px solid var(--border)",
                  background: "none", fontSize: 12, cursor: "pointer",
                }}
                data-testid="return-add-photo-btn"
              >
                {photos.length ? "Add another photo" : "Add a photo of the item"}
              </button>
            </div>
          )}

          {["requested", "declined"].includes(myRequest.status) && (
            myRequest.can_escalate ? (
              <button
                onClick={escalate}
                disabled={busy}
                style={{
                  marginTop: 10, padding: "7px 14px", borderRadius: 8, border: "none",
                  background: "#1e3a2a", color: "white", fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                }}
                data-testid="escalate-return-btn"
              >
                Ask our team to review
              </button>
            ) : myRequest.escalate_blocked ? (
              <div style={{ marginTop: 10, fontSize: 12, color: "var(--charcoal-mid)" }} data-testid="escalate-blocked-note">
                {myRequest.escalate_blocked}
              </div>
            ) : null
          )}
          {error && <div style={{ marginTop: 8, color: "var(--red)", fontSize: 12 }}>{error}</div>}
        </div>
      </div>
    );
  }

  const issueTypes = policy?.issue_types || [];

  return (
    <div style={{ padding: "6px 20px 14px" }}>
      {policy && (
        <p style={{ fontSize: 12, color: "var(--charcoal-light)", margin: "0 0 8px" }} data-testid={`return-policy-${receipt.order_number}`}>
          <strong>{receipt.vendor_store_name}:</strong>{" "}
          {policy.policy.accepts_returns
            ? `${policy.window_days}-day returns${policy.eligible ? ` · ${policy.closes_in_days} day(s) left` : ""}`
            : "doesn't accept returns"}
          {policy.policy.restocking_fee_percent > 0 && ` · ${policy.policy.restocking_fee_percent}% restocking fee`}
          {policy.policy.accepts_returns && ` · return shipping paid by ${policy.policy.return_shipping_paid_by}`}
          {policy.policy.non_returnable_note ? ` — ${policy.policy.non_returnable_note}` : ""}
        </p>
      )}
      {policy?.consumable_items?.length > 0 && (
        <p style={{ fontSize: 12, color: "var(--charcoal-light)", margin: "0 0 8px" }} data-testid="return-consumable-note">
          Consumables can&apos;t be returned once shipped.
        </p>
      )}
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          style={{
            padding: "7px 14px", borderRadius: 8, border: "1px solid var(--border)",
            background: "none", fontSize: 12.5, color: "var(--charcoal-mid)", cursor: "pointer",
          }}
          data-testid={`request-return-btn-${receipt.order_number}`}
        >
          {policy && !policy.eligible ? "Something wrong with this order?" : "Request a return"}
        </button>
      ) : (
        <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: 14 }}>
          <p style={{ fontSize: 12.5, color: "var(--charcoal-mid)", margin: "0 0 10px" }} data-testid="return-intro-note">
            {policy && !policy.eligible
              ? `Returns on this receipt are handled by ${store}, and this one falls outside their policy. Tell them what happened and they'll reply. If they decline, or don't answer within ${policy.escalation_after_days || 7} days, you can then ask our team to review it.`
              : `Returns on this receipt are handled by ${store}. Tell them what happened and they'll reply. If they decline, or don't answer within ${policy?.escalation_after_days || 7} days, you can then ask our team to review it.`}
          </p>
          <p style={{ fontSize: 12, color: "var(--charcoal-light)", margin: "0 0 10px" }} data-testid="return-scope-note">
            One request covers everything on this receipt — you don&apos;t need to send one per item.
            Leave the amount blank to ask for the full ${(receipt.subtotal || 0).toFixed(2)}.
          </p>

          <label style={{ display: "block", fontSize: 11.5, color: "var(--charcoal-light)", margin: "0 0 4px" }}>
            What went wrong?
          </label>
          <select
            value={issueType}
            onChange={e => setIssue(e.target.value)}
            style={{
              width: "100%", padding: "9px 12px", borderRadius: 9, border: "1.5px solid var(--border)",
              fontSize: 13, boxSizing: "border-box", outline: "none", background: "white",
              marginBottom: 10,
            }}
            data-testid="return-issue-select"
          >
            <option value="">Choose the closest one…</option>
            {issueTypes.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>

          <label style={{ display: "block", fontSize: 11.5, color: "var(--charcoal-light)", margin: "0 0 4px" }}>
            What would you rather have?
          </label>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            {[["refund", "A refund"], ["replacement", "A replacement"]].map(([value, text]) => (
              <button
                key={value}
                type="button"
                onClick={() => setWanted(value)}
                data-testid={`return-want-${value}`}
                style={{
                  padding: "8px 14px", borderRadius: 9, fontSize: 12.5, cursor: "pointer",
                  border: `1.5px solid ${wanted === value ? "#1e3a2a" : "var(--border)"}`,
                  background: wanted === value ? "#1e3a2a" : "none",
                  color: wanted === value ? "white" : "var(--charcoal-mid)",
                  fontWeight: wanted === value ? 600 : 400,
                }}
              >
                {text}
              </button>
            ))}
          </div>

          <textarea
            placeholder={`Tell ${store} what happened`}
            value={reason}
            onChange={e => setReason(e.target.value)}
            style={{
              width: "100%", minHeight: 64, padding: "9px 12px", borderRadius: 9,
              border: "1.5px solid var(--border)", fontFamily: "'Work Sans', sans-serif",
              fontSize: 13, boxSizing: "border-box", resize: "vertical", outline: "none",
            }}
            data-testid="return-reason-input"
          />
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
            <input
              placeholder={`Amount (max $${(receipt.subtotal || 0).toFixed(2)})`}
              value={amount}
              onChange={e => setAmount(e.target.value)}
              style={{
                width: 180, padding: "9px 12px", borderRadius: 9, border: "1.5px solid var(--border)",
                fontSize: 13, outline: "none",
              }}
              data-testid="return-amount-input"
            />
            <button
              onClick={submit}
              disabled={busy}
              style={{
                padding: "9px 18px", borderRadius: 9, border: "none", background: "#1e3a2a",
                color: "white", fontSize: 13, fontWeight: 600, cursor: "pointer",
              }}
              data-testid="submit-return-btn"
            >
              {busy ? "Sending..." : `Send it to ${store}`}
            </button>
            <button
              onClick={() => { setOpen(false); setError(""); }}
              style={{ padding: "9px 14px", borderRadius: 9, border: "1px solid var(--border)", background: "none", fontSize: 13, cursor: "pointer" }}
            >
              Cancel
            </button>
          </div>
          <p style={{ fontSize: 11.5, color: "var(--charcoal-light)", margin: "10px 0 0" }} data-testid="return-photo-hint">
            You&apos;ll be able to add photos once this is sent. A photo is required before our
            team will review anything.
          </p>
          {error && <div style={{ marginTop: 8, color: "var(--red)", fontSize: 12 }} data-testid="return-error">{error}</div>}
        </div>
      )}
    </div>
  );
}

// ─── Digital download ─────────────────────────────────────────────────────────

function DigitalDownload({ receipt, item, onDownloaded }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const limit = item.download_limit;
  const used = item.downloads_used || 0;

  const start = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await apiFetch(`/orders/receipts/${receipt.id}/download/${item.product_id}`);
      window.open(res.url, "_blank", "noopener");
      onDownloaded?.();
    } catch (e) {
      setError(e.message || "Could not start the download");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
      <button
        className="oh-download-btn"
        onClick={start}
        disabled={busy || !item.download_available}
        style={!item.download_available ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
        data-testid={`download-btn-${receipt.id}-${item.product_id}`}
      >
        <Download size={12} /> {busy ? "Preparing..." : "Download"}
      </button>
      <span style={{ fontSize: 11, color: "var(--charcoal-light)" }} data-testid={`download-count-${receipt.id}-${item.product_id}`}>
        {limit ? `${used} of ${limit} download${limit === 1 ? "" : "s"} used` : `${used} download${used === 1 ? "" : "s"}`}
      </span>
      {(error || item.download_blocked_reason) && (
        <span style={{ fontSize: 11, color: "var(--red)", maxWidth: 220, textAlign: "right" }} data-testid={`download-error-${receipt.id}-${item.product_id}`}>
          {error || item.download_blocked_reason}
        </span>
      )}
    </div>
  );
}

// ─── Order Card ───────────────────────────────────────────────────────────────

function OrderCard({ order, onReviewItem, reviewedProductIds, returnsBySub, onReturnsChanged }) {
  const [expanded, setExpanded] = useState(false);
  const sc = ORDER_STATUS_CONFIG[order.status] || ORDER_STATUS_CONFIG.pending;
  const StatusIcon = sc.icon;
  const canReview = ["paid", "delivered", "completed"].includes(order.status);
  // Etsy-style: one receipt per vendor. Falls back to raw splits for unpaid orders.
  const receipts = (order.sub_orders && order.sub_orders.length)
    ? order.sub_orders
    : (order.vendor_splits || []).map(s => ({
        id: s.vendor_id,
        vendor_id: s.vendor_id,
        order_number: null,
        vendor_store_name: s.vendor_name || "Storefront",
        status: s.fulfillment_status || "unfulfilled",
        items: (s.items || []).map(i => ({ ...i, price: i.price ?? i.price_snapshot })),
        subtotal: s.subtotal,
        tracking: s.tracking_number ? [{ carrier: "", tracking_number: s.tracking_number }] : [],
      }));

  // What the buyer actually paid: goods + postage + sales tax + card processing
  const pay = order.payment || {};
  const goods = pay.amount_total || 0;
  const shipping = pay.amount_shipping || 0;
  const tax = pay.amount_tax || 0;
  const processing = pay.amount_processing || 0;
  const charged = pay.amount_charged ?? (goods + shipping + tax + processing);

  return (
    <div className="oh-order-card" data-testid={`order-card-${order.id}`}>
      {/* Header row */}
      <div className="oh-order-head" onClick={() => setExpanded(v => !v)} data-testid="order-card-head">
        <div className="oh-order-head-left">
          <span className="oh-order-ref">Payment {order.order_number || formatRef(order.id)}</span>
          <p className="oh-order-date">{formatDate(order.created_at)}</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
            {receipts.map(r => (
              <span key={r.id} style={{ fontSize: 12, color: "var(--charcoal-light)" }}>
                {r.vendor_store_name} ({(r.items || []).length})
              </span>
            ))}
          </div>
        </div>
        <div className="oh-order-head-right">
          <span className="oh-order-total">${charged.toFixed(2)}</span>
          <span className={`oh-status ${sc.class}`}>
            <StatusIcon size={10} /> {sc.label}
          </span>
          <button className="oh-expand-btn">
            {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="oh-order-detail">
          {receipts.length > 1 && (
            <p style={{ fontSize: 12, color: "var(--charcoal-light)", padding: "12px 20px 0", margin: 0 }}>
              This payment covers {receipts.length} separate vendor receipts — each ships and is tracked on its own.
            </p>
          )}
          {receipts.map(receipt => {
            const fc = FULFILLMENT_CONFIG[receipt.status] || FULFILLMENT_CONFIG.unfulfilled;
            return (
              <div key={receipt.id} className="oh-vendor-split" data-testid={`order-receipt-${receipt.order_number || receipt.id}`}>
                <div className="oh-vendor-split-header">
                  <div className="oh-vendor-split-name-row">
                    <div className="oh-vendor-avatar">{(receipt.vendor_store_name || "S")[0]}</div>
                    <div>
                      <span className="oh-vendor-split-name">{receipt.vendor_store_name}</span>
                      {receipt.order_number && (
                        <div style={{ fontSize: 11, color: "var(--charcoal-light)", letterSpacing: "0.06em" }}>
                          Receipt {receipt.order_number}
                        </div>
                      )}
                    </div>
                  </div>
                  <span className={`oh-fulfillment-pill ${fc.class}`} data-testid="order-receipt-status">
                    {receipt.status === "shipped" ? <Truck size={10} /> : null}
                    {fc.label}
                  </span>
                </div>

                {/* Tracking */}
                {(receipt.tracking || []).map((t, i) => (
                  <div className="oh-tracking-info" key={i} data-testid="order-receipt-tracking">
                    <Truck size={13} />
                    <span>{t.carrier ? `${t.carrier} · ` : ""}Tracking: <strong>{t.tracking_number}</strong></span>
                    {t.url && (
                      <a href={t.url} target="_blank" rel="noreferrer" style={{ marginLeft: "auto", color: "inherit" }}>
                        <ExternalLink size={11} />
                      </a>
                    )}
                  </div>
                ))}

                {/* Items */}
                {(receipt.items || []).map((item, idx) => {
                  const ItemIcon = PRODUCT_ICONS[item.type] || Package;
                  return (
                    <div key={`${item.product_id}-${idx}`} className="oh-split-item">
                      <div className="oh-split-item-icon">
                        <ItemIcon size={18} />
                      </div>
                      <div className="oh-split-item-name">
                        <div>{item.title}</div>
                        <div className="oh-split-item-meta">
                          {item.type}{item.variant_name ? ` · ${item.variant_name}` : ""} · qty {item.qty}
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                        <span className="oh-split-item-price">${((item.price || 0) * item.qty).toFixed(2)}</span>
                        {item.is_digital && order.status !== "pending" && (
                          <DigitalDownload
                            receipt={receipt}
                            item={item}
                            onDownloaded={onReturnsChanged}
                          />
                        )}
                        {!item.is_digital && (item.type === "digital" || item.type === "course") && item.download_url && order.status !== "pending" && (
                          <a href={item.download_url} className="oh-download-btn" target="_blank" rel="noreferrer">
                            <Download size={12} /> Access
                          </a>
                        )}
                        {canReview && (
                          reviewedProductIds.has(item.product_id) ? (
                            <span
                              style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--charcoal-light)" }}
                              data-testid={`item-reviewed-${item.product_id}`}
                            >
                              <Star size={11} fill="#b07010" color="#b07010" /> Reviewed
                            </span>
                          ) : (
                            <button
                              className="oh-download-btn"
                              onClick={() => onReviewItem({
                                vendor_id:  receipt.vendor_id,
                                product_id: item.product_id,
                                order_id:   order.id,
                                title:      item.title,
                                store_name: receipt.vendor_store_name,
                              })}
                              data-testid={`review-item-btn-${item.product_id}`}
                            >
                              <Star size={12} /> Review this item
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  );
                })}

                {receipt.shipping_address && (
                  <div style={{ padding: "8px 20px 12px", fontSize: 12, color: "var(--charcoal-light)" }}>
                    Ships to {receipt.shipping_address.line1}, {receipt.shipping_address.city},{" "}
                    {receipt.shipping_address.state} {receipt.shipping_address.zip}
                  </div>
                )}

                {receipt.order_number && order.status !== "pending" && (
                  <ReturnRequestBlock
                    receipt={receipt}
                    myRequest={returnsBySub?.[receipt.id]}
                    onDone={onReturnsChanged}
                  />
                )}
              </div>
            );
          })}

          {/* Summary footer */}
          <div className="oh-order-summary-footer">
            <div className="oh-summary-amounts">
              <span style={{ fontSize: 13, color: "var(--charcoal-light)" }}>
                {receipts.reduce((s, r) => s + (r.items || []).length, 0)} item(s) · {receipts.length} vendor receipt(s)
              </span>
              <span style={{ fontSize: 13, color: "var(--charcoal-light)" }} data-testid="order-amount-items">
                Items: ${goods.toFixed(2)}
              </span>
              {shipping > 0 && (
                <span style={{ fontSize: 13, color: "var(--charcoal-light)" }} data-testid="order-amount-shipping">
                  Shipping: ${shipping.toFixed(2)}
                </span>
              )}
              {tax > 0 && (
                <span style={{ fontSize: 13, color: "var(--charcoal-light)" }} data-testid="order-amount-tax">
                  Sales tax: ${tax.toFixed(2)}
                </span>
              )}
              {processing > 0 && (
                <span style={{ fontSize: 13, color: "var(--charcoal-light)" }} data-testid="order-amount-processing">
                  Card processing: ${processing.toFixed(2)}
                </span>
              )}
              <span className="oh-summary-total" data-testid="order-amount-total">
                Total paid: ${charged.toFixed(2)}
              </span>
            </div>
            <span style={{ fontSize: 12, color: "var(--charcoal-light)" }}>
              {order.payment?.paid_at ? formatDate(order.payment.paid_at) : ""}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Item review modal ────────────────────────────────────────────────────────

function ItemReviewModal({ target, onClose, onSubmitted }) {
  const [rating, setRating] = useState(0);
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!rating) { setError("Pick a star rating first."); return; }
    setSaving(true);
    setError(null);
    try {
      await apiFetch("/reviews", {
        method: "POST",
        body: JSON.stringify({
          vendor_id:  target.vendor_id,
          product_id: target.product_id,
          order_id:   target.order_id,
          rating,
          body: body.trim() || null,
        }),
      });
      onSubmitted();
    } catch (e) {
      setError(e?.message || "Could not save your review.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)",
        zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "white", borderRadius: 16, padding: 28,
          maxWidth: 420, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        }}
        onClick={e => e.stopPropagation()}
        data-testid="item-review-modal"
      >
        <p style={{ fontFamily: "'Lora', serif", fontSize: 20, fontWeight: 600, margin: "0 0 6px" }}>
          Review {target.title}
        </p>
        <p style={{ fontSize: 13, color: "var(--charcoal-light)", margin: "0 0 20px" }}>
          Sold by {target.store_name || "this vendor"} · your rating counts toward this item and the
          seller's overall rating, and will be shown publicly.
        </p>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[1, 2, 3, 4, 5].map(n => (
            <Star
              key={n}
              size={28}
              color="#b07010"
              fill={n <= rating ? "#b07010" : "none"}
              style={{ cursor: "pointer" }}
              onClick={() => { setRating(n); setError(null); }}
              data-testid={`review-star-${n}`}
            />
          ))}
        </div>
        <textarea
          style={{
            width: "100%", minHeight: 80, padding: "10px 14px",
            border: "1.5px solid var(--border)", borderRadius: 9,
            fontFamily: "'Work Sans', sans-serif", fontSize: 14,
            outline: "none", resize: "vertical", boxSizing: "border-box",
          }}
          placeholder="Share your experience..."
          value={body}
          onChange={e => setBody(e.target.value)}
          data-testid="review-body-input"
        />
        {error && (
          <p style={{ color: "#a12b2b", fontSize: 13, margin: "10px 0 0" }} data-testid="review-error">
            {error}
          </p>
        )}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
          <button
            style={{ padding: "9px 18px", borderRadius: 8, border: "1px solid var(--border)", background: "none", fontSize: 13, cursor: "pointer" }}
            onClick={onClose}
            data-testid="review-cancel-btn"
          >
            Cancel
          </button>
          <button
            style={{ padding: "9px 20px", borderRadius: 8, border: "none", background: "#1e3a2a", color: "white", fontFamily: "'Work Sans', sans-serif", fontSize: 13, fontWeight: 600, cursor: saving ? "default" : "pointer", opacity: saving ? 0.6 : 1 }}
            onClick={submit}
            disabled={saving}
            data-testid="review-submit-btn"
          >
            {saving ? "Saving..." : "Submit review"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function OrderHistory({ onNavigateToMarketplace }) {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewedProductIds, setReviewedProductIds] = useState(new Set());
  const [returnsBySub, setReturnsBySub] = useState({});

  const loadOrders = () => {
    apiFetch("/orders?per_page=100").then((d) => setOrders(d?.orders || d || [])).catch(() => {});
  };

  const loadMyReviews = () => {
    apiFetch("/reviews/mine")
      .then((list) => setReviewedProductIds(new Set((list || []).map(r => r.product_id).filter(Boolean))))
      .catch(() => {});
  };

  const loadReturns = () => {
    apiFetch("/returns")
      .then((list) => {
        const map = {};
        (list || []).forEach((r) => {
          // newest request per receipt wins (list is newest-first)
          if (!map[r.sub_order_id]) map[r.sub_order_id] = r;
        });
        setReturnsBySub(map);
      })
      .catch(() => setReturnsBySub({}));
  };

  useEffect(() => { loadOrders(); loadReturns(); loadMyReviews(); }, []);

  const refreshAll = () => { loadOrders(); loadReturns(); };

  const filtered = orders.filter(o =>
    filter === "all" || o.status === filter
  );

  const counts = {
    all: orders.length,
    paid: orders.filter(o => ["paid", "processing", "shipped"].includes(o.status)).length,
    delivered: orders.filter(o => o.status === "delivered").length,
  };

  return (
    <>
      <style>{styles}</style>
      <div className="oh-root" data-testid="order-history-page">
        <div className="oh-header">
          <div>
            <h1>Order History</h1>
            <p>{orders.length} total order{orders.length !== 1 ? "s" : ""}</p>
          </div>
        </div>

        <div className="oh-filters">
          {[
            { id: "all",       label: `All (${counts.all})` },
            { id: "paid",      label: `Active (${counts.paid})` },
            { id: "delivered", label: `Delivered (${counts.delivered})` },
            { id: "cancelled", label: "Cancelled" },
          ].map(f => (
            <button
              key={f.id}
              className={`oh-filter-btn ${filter === f.id ? "active" : ""}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="oh-list">
          {filtered.length === 0 ? (
            <div className="oh-empty">
              <div className="oh-empty-icon"><ShoppingBag size={28} /></div>
              <p style={{ fontFamily: "'Lora', serif", fontSize: 20, fontWeight: 600, color: "var(--charcoal)", margin: "0 0 8px" }}>
                No orders yet
              </p>
              <p style={{ fontSize: 14, color: "var(--charcoal-light)", margin: "0 0 20px" }}>
                Your purchase history will appear here.
              </p>
              <button
                style={{
                  padding: "11px 24px", borderRadius: 10,
                  background: "#1e3a2a", border: "none", color: "white",
                  fontFamily: "'Work Sans', sans-serif", fontSize: 14, fontWeight: 600,
                  cursor: "pointer",
                }}
                onClick={() => (onNavigateToMarketplace
                  ? onNavigateToMarketplace()
                  : window.location.assign("/dashboard/marketplace"))}
              >
                Browse the Marketplace
              </button>
            </div>
          ) : (
            filtered.map(order => (
              <OrderCard
                key={order.id}
                order={order}
                onReviewItem={setReviewTarget}
                reviewedProductIds={reviewedProductIds}
                returnsBySub={returnsBySub}
                onReturnsChanged={refreshAll}
              />
            ))
          )}
        </div>

        {reviewTarget && (
          <ItemReviewModal
            target={reviewTarget}
            onClose={() => setReviewTarget(null)}
            onSubmitted={() => {
              setReviewTarget(null);
              loadMyReviews();
            }}
          />
        )}
      </div>
    </>
  );
}
